import { useCallback, useState } from 'react';

import { useModal, useToastr } from '@farmshare/ui-components';
import { Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { vendorState } from 'state';

import {
  EnumUserType,
  User,
  useWholesaleSetCustomerEnabledMutation,
} from '../../../../lib/graphql';

import { CustomerForm } from '../customer-form/customer-form';

export interface AddCustomerModalOptions {
  /** Existing customer to edit; omit to create a new one. */
  customer?: User | null;
  /** Pin the relationship type (e.g. Producer) instead of letting the user pick. */
  forcedType?: EnumUserType;
  /** Called with the created/updated customer after a successful save. */
  onSuccess?: (created: User) => void | Promise<void> | undefined;
  /** Header text. Defaults to "Add Customer". */
  title?: string;
  vendorId?: string;
  /**
   * Offer a "Wholesale customer" checkbox (new customers only). Pass when the
   * plant takes wholesale orders.
   */
  offerWholesale?: boolean;
}

/**
 * The "Wholesale customer" checkbox above a new customer's form. It rides the
 * form's own Save: once the customer is created, they're made a wholesale
 * customer, so there's no second button to press.
 * @param props - Props
 * @param props.render - Renders the form, given the success handler to use
 * @param props.onSuccess - The caller's own success handler
 * @param props.vendorId - The vendor, when not the current one
 * @returns The checkbox and the form
 */
function WithWholesaleCheckbox({
  render,
  onSuccess,
  vendorId,
}: {
  render: (onSuccess: (created: User) => Promise<void>) => React.ReactNode;
  onSuccess?: AddCustomerModalOptions['onSuccess'];
  vendorId?: string;
}) {
  const vendor = useRecoilValue(vendorState);
  const { push } = useToastr();
  const [wholesale, setWholesale] = useState(false);
  const [setEnabled] = useWholesaleSetCustomerEnabledMutation();
  const handleSuccess = async (created: User) => {
    if (wholesale && created?._id) {
      try {
        await setEnabled({
          variables: {
            vendorId: vendorId ?? vendor?._id,
            customerId: created._id,
            enabled: true,
          },
        });
      } catch (err) {
        push({
          title: 'Customer saved, but not as wholesale',
          body: `${(err as Error).message} Turn on "Sells wholesale" on their page to finish.`,
          bg: 'warning',
          delay: 8000,
        });
      }
    }
    await onSuccess?.(created);
  };
  return (
    <>
      <FormControlLabel
        control={
          <Checkbox
            checked={wholesale}
            onChange={(e) => setWholesale(e.target.checked)}
          />
        }
        label={
          <>
            Wholesale customer
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{ display: 'block' }}
            >
              A restaurant or store that buys product on wholesale orders.
            </Typography>
          </>
        }
        sx={{
          mb: 1,
          alignItems: 'flex-start',
          '& .MuiCheckbox-root': { pt: 0.5 },
        }}
      />
      {render(handleSuccess)}
    </>
  );
}

/**
 * Returns a function that opens the add/edit customer modal.
 *
 * `info` rather than `save`: the form inside is `CustomerForm`, which owns its
 * own Formik instance and its own Save/Cancel row, so the modal contributes the
 * chrome only — hence `footer: null`, which suppresses the default Ok button.
 * @returns A callback that opens the modal.
 */
export function useAddCustomerModal() {
  const { info } = useModal();

  return useCallback(
    ({
      customer = null,
      forcedType,
      onSuccess,
      title = 'Add Customer',
      vendorId,
      offerWholesale = false,
    }: AddCustomerModalOptions = {}) => {
      info({
        type: 'info',
        title,
        size: 'sm',
        // A full customer form (name, contact, address, hang-time fields) is
        // unusable in a centered card on a phone. Fullscreen there is already
        // the `info` default (see IModalBase.fullscreenOnMobile); stated here
        // because this modal depends on it.
        fullscreenOnMobile: true,
        // CustomerForm renders its own Save/Cancel; null suppresses the Ok
        // button the `info` type would otherwise add below them.
        footer: null,
        // No aria-label needed: the shared renderer renders the title as an
        // <h2> and labels the dialog by it, as this dialog's own markup used to.
        // Function body, not a plain node, so the form's Cancel and post-save
        // close run the renderer's own close (which animates out) rather than
        // the provider's `destroy` (which yanks the dialog off-screen).
        body: (modalProps) => {
          const form = (
            handleSuccess: AddCustomerModalOptions['onSuccess'],
          ) => (
            <CustomerForm
              customer={customer}
              forcedType={forcedType}
              onSuccess={handleSuccess}
              onCancel={() => modalProps.onClose?.()}
              onClose={() => modalProps.onClose?.()}
              // The form runs its own create/update, which the modal can't see.
              setBusy={(busy) => modalProps.setBusy?.(busy)}
              isInDialog
              showActions
              idPrefix="add-customer"
              vendorId={vendorId}
            />
          );
          return offerWholesale && !customer ? (
            <WithWholesaleCheckbox
              render={form}
              onSuccess={onSuccess}
              vendorId={vendorId}
            />
          ) : (
            form(onSuccess)
          );
        },
      });
    },
    [info],
  );
}
